// src/modules/app-views/_apps/appsindico/units/pages/Init/modals/UnitsParametersModal/tabs/ProfilesTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Button,
  Flex,
  Space,
  Popconfirm,
  Modal,
  Form,
  Input,
  InputNumber,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  SlidersOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { generateUidV4 } from 'utils/helpers';
import useZustand from 'hooks/useZustand';
import SimpleTable from 'components/SimpleComponents/SimpleTable';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ProfilesTab = ({ loading, setLoading }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedModule = useZustand('module', 'config');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [mousePosition, setMousePosition] = useState(null);

  const [profiles, setProfiles] = useState(() => {
    const moduleId = storedModule?.id || 'units';
    const raw = appService.getModulePreferences({ moduleId, category: 'profiles' });
    return Array.isArray(raw) ? raw : [];
  });

  const visibleProfiles = useMemo(() => profiles.filter(p => !p.removed), [profiles]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (showModal) {
      if (editingRecord) {
        form.setFieldsValue({
          name: editingRecord.name || '',
          m2: editingRecord.m2 ?? null,
          unitDigits: editingRecord.unitDigits ?? null,
        });
      } else {
        form.resetFields();
      }
    }
  }, [showModal, editingRecord, form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAdd = useCallback(e => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((record, e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(record);
    setShowModal(true);
  }, []);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setEditingRecord(null);
    form.resetFields();
  }, [form]);

  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const moduleId = storedModule?.id || 'units';

      const nameValue = (values.name || '').trim();

      // Check for duplicate name
      const isDuplicate = profiles.some(
        p =>
          p.uid !== editingRecord?.uid &&
          !p.removed &&
          (p.name || '').toLowerCase() === nameValue.toLowerCase()
      );

      if (isDuplicate) {
        appService.message({ flag: 'w', text: 'item_already_exists' });
        return;
      }

      setLoading(true);

      let updated;
      if (editingRecord) {
        updated = profiles.map(p =>
          p.uid === editingRecord.uid
            ? {
                ...p,
                name: nameValue,
                m2: values.m2 ?? p.m2,
                unitDigits: values.unitDigits ?? p.unitDigits,
              }
            : p
        );
      } else {
        updated = [
          ...profiles,
          {
            uid: generateUidV4(),
            name: nameValue,
            m2: values.m2 ?? null,
            unitDigits: values.unitDigits ?? 4,
          },
        ];
      }

      await appService.updateModulePreferences({ moduleId, category: 'profiles', data: updated });

      setProfiles(updated);
      appService.message({ flag: 's', text: 'saved_successfully' });
      setShowModal(false);
      setEditingRecord(null);
      form.resetFields();
    } catch (error) {
      if (error?.errorFields) return; // validation error
      console.error('[ProfilesTab] Save error:', error);
      appService.message({ flag: 'e', text: 'error_saving' });
    } finally {
      setLoading(false);
    }
  }, [form, profiles, editingRecord, setLoading, storedModule?.id]);

  const handleDelete = useCallback(
    async record => {
      const moduleId = storedModule?.id || 'units';
      setLoading(true);
      try {
        const isInUse = await appService.checkParameterInUse({
          module: moduleId,
          category: 'profiles',
          uid: record.uid,
        });

        let updated;
        if (isInUse) {
          updated = profiles.map(p =>
            p.uid === record.uid ? { ...p, removed: true } : p
          );
          appService.message({ flag: 'w', text: 'item_in_use_hidden' });
        } else {
          updated = profiles.filter(p => p.uid !== record.uid);
          appService.message({ flag: 's', text: 'removed_successfully' });
        }

        await appService.updateModulePreferences({ moduleId, category: 'profiles', data: updated });
        setProfiles(updated);
      } catch (error) {
        console.error('[ProfilesTab] Delete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [profiles, setLoading, storedModule?.id]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: translateX('profile'),
      dataIndex: ['name'],
    },
    {
      title: translateX('m2'),
      dataIndex: ['m2'],
      width: 80,
      align: 'center',
    },
    {
      title: translateX('unit_digits'),
      dataIndex: ['unitDigits'],
      width: 80,
      align: 'center',
    },
    {
      title: translateX('actions'),
      dataIndex: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={e => handleEdit(record, e)}
            disabled={loading}
          />
          <Popconfirm
            title={translateX('confirm_delete')}
            onConfirm={() => handleDelete(record)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={loading} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex vertical style={{ height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <SimpleTable
          rowKey="uid"
          columns={columns}
          dataSource={visibleProfiles}
          pagination={false}
          size="small"
          loading={loading}
          locale={{ emptyText: translateX('no_data') }}
          scroll={{ y: 'calc(80vh - 240px)' }}
        />
      </div>

      <Flex justify="flex-end" className="mt-3" style={{ flexShrink: 0 }}>
        <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_profile')}
        </Button>
      </Flex>

      <Modal
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SlidersOutlined />
            {editingRecord ? translateX('edit_profile') : translateX('add_profile')}
          </span>
        }
        open={showModal}
        onCancel={handleCancel}
        destroyOnHidden
        centered
        mousePosition={mousePosition}
        getContainer={document.body}
        width={400}
        footer={
          <Flex justify="end" style={{ paddingTop: 8 }}>
            <Space>
              <Button onClick={handleCancel} disabled={loading} icon={<CloseOutlined />}>
                {translateX('cancel')}
              </Button>
              <Button type="primary" loading={loading} icon={<CheckOutlined />} onClick={handleSave}>
                {translateX('save')}
              </Button>
            </Space>
          </Flex>
        }
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Row gutter={12}>
            {/* NAME */}
            <Col span={24}>
              <Form.Item
                name="name"
                label={translateX('name')}
                rules={[{ required: true, message: () => translateX('required_field') }]}
              >
                <Input maxLength={80} disabled={loading} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            {/* M2 */}
            <Col span={12}>
              <Form.Item
                name="m2"
                label={translateX('m2')}
                rules={[{ required: true, message: () => translateX('required_field') }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} disabled={loading} />
              </Form.Item>
            </Col>

            {/* UNIT DIGITS */}
            <Col span={12}>
              <Form.Item
                name="unitDigits"
                label={translateX('unit_digits')}
                tooltip={translateX('unit_digits_info')}
                rules={[{ required: true, message: () => translateX('required_field') }]}
              >
                <InputNumber min={1} max={10} style={{ width: '100%' }} disabled={loading} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ProfilesTab;
